/**
 * Watchdog Context
 *
 * Provides a single shared Firestore listener on `controllers/{id}/ai_state/watchdog`
 * instead of having 4 separate onSnapshot calls across:
 *   - useHAStatus (SystemTab)
 *   - AIControlPage
 *   - FloatingChatWidget
 *   - AIActivityPanel
 *
 * Mount <WatchdogProvider> in App.tsx. All consumers call useWatchdog().
 */

import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, CONTROLLER_ID } from '../firebase';
import type { WatchdogState } from '../hooks/useHAStatus';

// ============================================================================
// Types
// ============================================================================

interface WatchdogContextValue {
  watchdogState: WatchdogState | null;
  loading: boolean;
}

// ============================================================================
// Context
// ============================================================================

const WatchdogContext = createContext<WatchdogContextValue | null>(null);

export function useWatchdog(): WatchdogContextValue {
  const ctx = useContext(WatchdogContext);
  if (!ctx) {
    throw new Error('useWatchdog must be used within a WatchdogProvider');
  }
  return ctx;
}

// ============================================================================
// Provider
// ============================================================================

export function WatchdogProvider({ children }: { children: ReactNode }) {
  const [watchdogState, setWatchdogState] = useState<WatchdogState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const watchdogRef = doc(db, 'controllers', CONTROLLER_ID, 'ai_state', 'watchdog');
    const unsubscribe = onSnapshot(
      watchdogRef,
      (snap) => {
        if (snap.exists()) {
          setWatchdogState(snap.data() as WatchdogState);
        } else {
          setWatchdogState(null);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const value = useMemo(() => ({ watchdogState, loading }), [watchdogState, loading]);

  return (
    <WatchdogContext.Provider value={value}>
      {children}
    </WatchdogContext.Provider>
  );
}

export default WatchdogProvider;
